import React from 'react';
import { Link } from 'react-router-dom';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import LockOutlined from '@mui/icons-material/LockOutlined';
import LockOpenOutlined from '@mui/icons-material/LockOpenOutlined';

import { useStyles } from './component.style';
import { SampleTypeCodeEnum } from '../../../../../../../../core/api/lab.types';

export interface CryoboxRowProps {
  serialNo: string;
  sampleType: SampleTypeCodeEnum;
  freezerName: string;
  freezerShelfNo: string;
  shelfRackNo: string;
  rackPositionNo: string;
  editLink: string;
  searchTerm: string | null;
  aliquotPositions: string[] | null;
  locked?: boolean;
}

function getMatchedSlot(searchTerm: string | null, aliquotPositions: string[] | null) {
  if (!searchTerm || !aliquotPositions) {
    return null;
  }

  const matchedIndex = aliquotPositions.lastIndexOf(searchTerm);

  if (matchedIndex < 0) {
    return null;
  }

  return matchedIndex + 1;
}

function CryoboxRow({
  serialNo,
  sampleType,
  freezerName,
  freezerShelfNo,
  shelfRackNo,
  rackPositionNo,
  editLink,
  searchTerm,
  aliquotPositions,
  locked,
}: CryoboxRowProps) {
  const { classes } = useStyles();

  const status = locked ? 'LOCKED' : 'UNLOCKED';
  const matchedSlot = getMatchedSlot(searchTerm, aliquotPositions);

  const statusTone = locked
    ? {
        chipColor: 'warning' as const,
        rowBgcolor: 'warning.lighter',
        borderColor: 'warning.main',
        icon: <LockOutlined />,
        tooltip: 'This cryobox is locked and cannot be edited.',
      }
    : {
        chipColor: 'success' as const,
        rowBgcolor: 'background.paper',
        borderColor: 'success.main',
        icon: <LockOpenOutlined />,
        tooltip: 'This cryobox is unlocked and can be edited.',
      };

  return (
    <TableRow
      key={serialNo}
      data-testid="result"
      sx={{
        bgcolor: statusTone.rowBgcolor,
        borderLeft: 4,
        borderLeftColor: statusTone.borderColor,
        transition: 'background-color 150ms ease',
        '&:hover': {
          bgcolor: locked ? 'warning.lighter' : 'grey.50',
        },
        '& td': {
          borderBottomColor: 'divider',
        },
      }}
    >
      <TableCell align="left">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Link to={editLink} className={classes.compact}>
            <Typography component="span" variant="body2" color="primary" sx={{ fontWeight: 700 }}>
              {serialNo}
            </Typography>
          </Link>

          <Typography variant="caption" component="span" color="text.secondary">
            Cryobox
          </Typography>
        </Box>
      </TableCell>

      <TableCell align="center">
        <Chip
          label={sampleType}
          size="small"
          variant="outlined"
          sx={{
            fontWeight: 700,
            minWidth: 72,
          }}
        />
      </TableCell>

      <TableCell align="center">
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {freezerName}
        </Typography>
      </TableCell>

      <TableCell align="center">{freezerShelfNo}</TableCell>

      <TableCell align="center">{shelfRackNo}</TableCell>

      <TableCell align="center">{rackPositionNo}</TableCell>

      <TableCell align="center" data-testid="slot">
        {matchedSlot ? (
          <Chip
            label={`Slot ${matchedSlot}`}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 700 }}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        )}
      </TableCell>

      <TableCell align="center">
        <Tooltip title={statusTone.tooltip}>
          <Chip
            icon={statusTone.icon}
            label={status}
            size="small"
            color={statusTone.chipColor}
            sx={{
              fontWeight: 700,
              minWidth: 104,
              '& .MuiChip-icon': {
                fontSize: 18,
              },
            }}
          />
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

export default CryoboxRow;
